import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import getClient from "../../helpers/get-client";

/**
 * CulturesAlertContext — alarma global de cultivos nuevos (V1 frontend-only).
 *
 * Idea: cuando el usuario descarga/actualiza exámenes de un paciente y aparece
 * un cultivo nuevo (o uno pendiente pasa a positivo), se dispara un popup global
 * — esté en la vista que esté — y queda registrado en la campana del navbar.
 *
 * Detección sin backend nuevo: mantenemos un snapshot en localStorage de los
 * cultivos ya conocidos por RUT. Tras cada descarga, `checkPatientCultures`
 * refetchea el grupo "Cultivos" de /widgets/ y compara contra el snapshot. Lo
 * que no estaba y es clínicamente relevante (positivo o pendiente/en curso) se
 * agrega a la cola de alertas.
 *
 * Regla anti-spam: el PRIMER encuentro con un paciente solo siembra el snapshot
 * (no avisa) — un cultivo solo es "nuevo" respecto de algo que ya viste. El
 * estado "visto" es por dispositivo/navegador (localStorage); una V2 con backend
 * lo haría por usuario y cross-device.
 */

const SNAPSHOT_KEY = "smartdx.cultureAlerts.snapshot.v1";
const QUEUE_KEY = "smartdx.cultureAlerts.queue.v1";
const MAX_QUEUE = 40;

const CulturesAlertContext = createContext(null);

// Estados de cultivo que vale la pena avisar. "rejected"/"normal" (negativo) no.
const RELEVANT_STATUS = new Set(["high", "warning"]);

const readJson = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota / modo privado — la feature degrada silenciosa */
  }
};

// En el primer encuentro con un paciente solo avisamos cultivos "frescos"
// (tomados hace poco), para no spamear con el historial viejo pero sí capturar
// un positivo/pendiente reciente que el médico está viendo por primera vez.
const FRESH_DAYS = 3;

const isFresh = (fechaToma) => {
  const s = String(fechaToma || "").trim();
  if (!s) return false;
  const t = Date.parse(s.replace(" ", "T"));
  if (Number.isNaN(t)) return false;
  return (Date.now() - t) / 86400000 <= FRESH_DAYS;
};

// Identidad estable de un cultivo dentro de un paciente. Incluye status+valor
// para que un cambio de estado (pendiente → germen) cuente como "nuevo".
const cultureKey = (exam) => {
  const cd = exam?.culture_detail || {};
  const fecha = String(cd.fecha_toma || "").slice(0, 16);
  return [
    String(exam?.name || exam?.abbrev || "").trim().toLowerCase(),
    fecha,
    exam?.status || "",
    String(exam?.last_value || "").trim().toLowerCase(),
  ].join("|");
};

export function CulturesAlertProvider({ children }) {
  const client = useMemo(() => getClient(), []);
  const [alerts, setAlerts] = useState(() => readJson(QUEUE_KEY, []));
  // Cola efímera de popups a mostrar (no persiste — solo lo detectado en vivo).
  const [popupBatch, setPopupBatch] = useState(null);
  const snapshotRef = useRef(readJson(SNAPSHOT_KEY, {}));
  const inFlightRef = useRef(new Set()); // ruts con check en curso (dedup)

  // Persistir la cola cada vez que cambia.
  useEffect(() => {
    writeJson(QUEUE_KEY, alerts.slice(0, MAX_QUEUE));
  }, [alerts]);

  const persistSnapshot = useCallback(() => {
    writeJson(SNAPSHOT_KEY, snapshotRef.current);
  }, []);

  /**
   * Refetchea los cultivos de un paciente y detecta los nuevos relevantes.
   * Llamar tras cada descarga/actualización de exámenes (onJobComplete).
   */
  const checkPatientCultures = useCallback(
    async (rut, patientName) => {
      const cleanRut = String(rut || "").trim();
      if (!cleanRut || inFlightRef.current.has(cleanRut)) return;
      inFlightRef.current.add(cleanRut);
      try {
        const res = await client.post(
          "/api/medical_analysis/exams/visualizer/widgets/",
          { rut: cleanRut },
        );
        const groups = res?.data?.groups || [];
        const cultGroup = groups.find((g) => g?.name === "Cultivos");
        const cultures = (cultGroup?.exams || []).filter((e) => e?.is_culture);
        if (cultures.length === 0) return;

        const currentKeys = cultures.map(cultureKey);
        const known = snapshotRef.current[cleanRut];
        const firstEncounter = !Array.isArray(known);
        const knownSet = new Set(Array.isArray(known) ? known : []);

        // Qué avisar:
        //  - primer encuentro → solo relevantes y RECIENTES (evita spam del historial)
        //  - encuentros siguientes → cualquier relevante que no estaba en el snapshot
        const freshExams = cultures.filter((e, i) => {
          if (!RELEVANT_STATUS.has(e?.status)) return false;
          if (firstEncounter) return isFresh(e?.culture_detail?.fecha_toma);
          return !knownSet.has(currentKeys[i]);
        });

        // Actualizar snapshot con TODO lo actual (incl. lo ya conocido).
        snapshotRef.current[cleanRut] = currentKeys;
        persistSnapshot();

        if (freshExams.length === 0) return;

        const now = Date.now();
        const newAlerts = freshExams.map((e) => {
          const cd = e.culture_detail || {};
          return {
            id: `${cleanRut}::${cultureKey(e)}`,
            rut: cleanRut,
            patientName: patientName || "",
            tipo: e.name || "",
            abbrev: e.abbrev || "",
            germen: e.last_value || "",
            status: e.status, // high | warning
            fechaToma: cd.fecha_toma || "",
            detectedAt: now,
          };
        });

        setAlerts((prev) => {
          const existing = new Set(prev.map((a) => a.id));
          const merged = [
            ...newAlerts.filter((a) => !existing.has(a.id)),
            ...prev,
          ];
          return merged.slice(0, MAX_QUEUE);
        });
        // Disparar popup con lo recién detectado (solo lo que no estaba ya en cola).
        setPopupBatch(newAlerts);
      } catch {
        /* red/perms — no romper el flujo de descarga */
      } finally {
        inFlightRef.current.delete(cleanRut);
      }
    },
    [client, persistSnapshot],
  );

  const markSeen = useCallback((id) => {
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const markAllSeen = useCallback(() => {
    setAlerts([]);
  }, []);

  const dismissPopup = useCallback(() => {
    setPopupBatch(null);
  }, []);

  const value = useMemo(
    () => ({
      alerts,
      unseenCount: alerts.length,
      popupBatch,
      checkPatientCultures,
      markSeen,
      markAllSeen,
      dismissPopup,
    }),
    [alerts, popupBatch, checkPatientCultures, markSeen, markAllSeen, dismissPopup],
  );

  return (
    <CulturesAlertContext.Provider value={value}>
      {children}
    </CulturesAlertContext.Provider>
  );
}

export function useCulturesAlerts() {
  const ctx = useContext(CulturesAlertContext);
  if (!ctx) {
    // Fuera del provider: no-op seguro para que los call-sites no revienten.
    return {
      alerts: [],
      unseenCount: 0,
      popupBatch: null,
      checkPatientCultures: async () => {},
      markSeen: () => {},
      markAllSeen: () => {},
      dismissPopup: () => {},
    };
  }
  return ctx;
}

export default CulturesAlertContext;
